import useTypedContext from "hooks/useTypedContext";
import FlashContext from "components/FlashMessages/FlashContext";
import useLockStack from "shared/Stack/useLockStack";
import useUnlockStack from "shared/Stack/useUnlockStack";
import useTriggerStack from "shared/Stack/useTriggerStack";
import useSetStackFavorite from "shared/Stack/useSetStackFavorite";
import DropdownMenuEllipsis from "ds/components/DropdownMenu/Ellipsis";
import DropdownMenuItem from "ds/components/DropdownMenu/Item";
import CopyFieldDropdownMenuItem from "components/CopyField/DropdownMenuItem";
import { Stack } from "types/generated";
import { TableSimpleCellProps } from "ds/components/TableSimple/types";
import TableSimpleCell from "ds/components/TableSimple/Cell";
import useCanUnlockStack from "shared/Stack/access/useCanUnlockStack";
import useCanTriggerStack from "shared/Stack/access/useCanTriggerStack";
import useCanLockStack from "shared/Stack/access/useCanLockStack";

import { ANALYTICS } from "../../constants";

const REFETCH_QUERIES = ["SearchStacksNew", "SearchStacksSuggestionsNew"];

const StacksTableRowActionsCell = ({ item: stack, ...cellProps }: TableSimpleCellProps<Stack>) => {
  const { reportSuccess } = useTypedContext(FlashContext);

  const { setStackFavorite } = useSetStackFavorite({
    refetchQueries: REFETCH_QUERIES,
  });

  const [lockStack] = useLockStack({
    refetchQueries: REFETCH_QUERIES,
  });

  const [stackUnlock] = useUnlockStack({
    refetchQueries: REFETCH_QUERIES,
  });

  const [runTrigger] = useTriggerStack();

  const canTrigger = useCanTriggerStack()(stack);
  const canLockStack = useCanLockStack()(stack);
  const canUnlockStack = useCanUnlockStack()(stack);

  const handleTriggerStack = () => {
    runTrigger({ stack: stack.id, runtimeConfig: null }, (data) => {
      if (data?.runTrigger.id) {
        reportSuccess({ message: "Run successfully triggered" });
      }
    });
  };

  const handleLockStack = () => {
    lockStack({ id: stack.id, note: null }, (data) => {
      if (data?.stackLock.id) {
        reportSuccess({ message: "Stack successfully locked" });
      }
    });
  };

  const handleUnlockStack = () => {
    stackUnlock({ id: stack.id }, (data) => {
      if (data?.stackUnlock.id) {
        reportSuccess({ message: "Stack successfully unlocked" });
      }
    });
  };

  const handleStarStack = () => {
    setStackFavorite({ id: stack.id, star: !stack.starred });
  };

  return (
    <TableSimpleCell {...cellProps}>
      <DropdownMenuEllipsis tooltip="Stack actions">
        {(canTrigger.result || canTrigger.message) && (
          <DropdownMenuItem
            onAction={handleTriggerStack}
            tooltip={canTrigger.message}
            isDisabled={!!canTrigger.message && !canTrigger.result}
            tooltipPlacement="left"
          >
            Trigger run
          </DropdownMenuItem>
        )}
        {(canLockStack.result || canLockStack.message) && (
          <DropdownMenuItem
            onAction={handleLockStack}
            tooltip={canLockStack.message}
            isDisabled={!!canLockStack.message && !canLockStack.result}
            tooltipPlacement="left"
          >
            Lock stack
          </DropdownMenuItem>
        )}
        {(canUnlockStack.result || canUnlockStack.message) && (
          <DropdownMenuItem
            onAction={handleUnlockStack}
            tooltip={canUnlockStack.message}
            isDisabled={!!canUnlockStack.message && !canUnlockStack.result}
            tooltipPlacement="left"
          >
            Unlock stack
          </DropdownMenuItem>
        )}
        <CopyFieldDropdownMenuItem title="Copy ID" value={stack.id} />
        <DropdownMenuItem
          {...ANALYTICS}
          analyticsTitle="Star clicked"
          analyticsProps={{
            value: stack.starred ? "remove" : "add",
          }}
          onAction={handleStarStack}
        >
          {stack.starred ? "Remove from favorites" : "Add to favorites"}
        </DropdownMenuItem>
        <DropdownMenuItem href={`/stack/${stack.id}/settings`}>Settings</DropdownMenuItem>
      </DropdownMenuEllipsis>
    </TableSimpleCell>
  );
};

export default StacksTableRowActionsCell;
